import { Button } from '@mm/components';
import { ReactComponent as StepError } from 'assets/baseline-error_outline.svg';
import { ReactComponent as StepComplete } from 'assets/messenger-ads-icons/checked.svg';
import { ReactComponent as StepNotComplete } from 'assets/messenger-ads-icons/ellipse.svg';
import classnames from 'classnames';
import { times } from 'lodash';
import React, {
  createRef,
  MutableRefObject,
  ReactNode,
  useCallback,
  useState,
} from 'react';
import { CollapsibleContentContainerWithoutLocalState } from '../../../../../../common/components/CollapsibleContentContainer/CollapsibleContentContainerWithoutLocalState';
import { PageBillingType } from '@mm/state/interfaces/connected-page';
import './FormStepContainer.pcss';
import { ButtonColor } from '../../../../../../common/buttons/PrimaryButton/PrimaryButton';

type Props = {
  valid: boolean;
  onNext?: (allowed?: boolean) => void;
  children: ReactNode;
  index: number;
  header: string | ReactNode;
  touched?: boolean;
  nextAllowed?: boolean;
  className?: string;
  contentClassName?: string;
  last?: boolean;
  collapsed?: boolean;
  dropdownMenuContent?: JSX.Element;
  renderDropdownOptions?: () => JSX.Element;
  useCrossIconStyle?: boolean;
  showOrderNumber?: boolean;
  proBadgeType?: PageBillingType;
  stepsStatus: boolean[];
  indexOffset?: number;
  setStepsStatus: (steps: boolean[]) => void;
  hideNextButton?: boolean;
  onStepOpened?: (index: number) => void;
  customButtonColor?: ButtonColor;
  customOnCollapsibleClicked?: (index: number) => void;
};

type FormOptions = {
  isCollapsed: (index: number) => boolean;
  onNext: (index: number, allowed?: boolean) => void;
  nextAllowed: (index: number) => boolean;
  isValid: (index: number) => boolean;
  isTouched: (index: number) => boolean;
  contentClassName: string;
  className: string;
  indexOffset?: number;
  useCrossIconStyle?: boolean;
  stepsStatus: boolean[];
  setStepsStatus: (steps: boolean[]) => void;
  onStepOpened?: (index: number) => void;
  customButtonColor?: ButtonColor;
  customOnCollapsibleClicked?: (index: number) => void;
};

export type FormStepConfig = {
  id?: string;
  className?: string;
  contentClassName?: string;
  children: ReactNode;
  renderDropdownOptions?: () => JSX.Element;
  header: string | ReactNode;
  dropdownMenuContent?: JSX.Element;
  showOrderNumber?: boolean;
  proBadgeType?: PageBillingType;
  hideNextButton?: boolean;
};

type ContainerBuilderParams = {
  configs: FormStepConfig[];
  options: FormOptions;
};

export const formStepContainerBuilder = ({
  configs,
  options,
}: ContainerBuilderParams) => {
  const length = configs.length - 1;
  const refs: MutableRefObject<HTMLDivElement | null>[] = times(length, () =>
    createRef<HTMLDivElement>(),
  );

  return (
    <div>
      {configs.map((config, index) => {
        const nextAllowed = options.nextAllowed(index);
        return (
          <FormStepContainer
            key={config.id || index}
            ref={index === 0 ? null : refs[index - 1]}
            valid={options.isValid(index)}
            index={index + 1}
            indexOffset={options.indexOffset}
            touched={options.isTouched(index)}
            header={config.header}
            contentClassName={
              config.contentClassName || options.contentClassName
            }
            renderDropdownOptions={config.renderDropdownOptions}
            className={config.className || options.className}
            collapsed={options.isCollapsed(index)}
            nextAllowed={nextAllowed}
            last={index === length}
            onNext={(allowed?: boolean) => {
              const ref = refs[index];
              options.onNext(index, allowed);
              if (ref && ref.current !== null && allowed) {
                const updatedStepsStatus = [...options.stepsStatus];
                updatedStepsStatus[index + 1] = true;
                options.setStepsStatus(updatedStepsStatus);
                const current: HTMLDivElement = ref.current;
                setTimeout(() => current.scrollIntoView(false), 0);
              }
            }}
            dropdownMenuContent={config.dropdownMenuContent}
            useCrossIconStyle={options.useCrossIconStyle}
            showOrderNumber={config.showOrderNumber}
            proBadgeType={config.proBadgeType}
            stepsStatus={options.stepsStatus}
            setStepsStatus={options.setStepsStatus}
            hideNextButton={config.hideNextButton}
            onStepOpened={options.onStepOpened}
            customButtonColor={options.customButtonColor}
            customOnCollapsibleClicked={options.customOnCollapsibleClicked}
          >
            {config.children}
          </FormStepContainer>
        );
      })}
    </div>
  );
};

const FormStepContainer = React.forwardRef(
  (props: Props, ref: MutableRefObject<HTMLDivElement | null>) => {
    const {
      valid,
      onNext,
      children,
      index,
      header,
      touched,
      nextAllowed,
      className,
      contentClassName,
      last,
      collapsed,
      dropdownMenuContent,
      renderDropdownOptions,
      useCrossIconStyle,
      indexOffset = 0,
      showOrderNumber = true,
      proBadgeType,
      stepsStatus,
      setStepsStatus,
      hideNextButton,
      onStepOpened,
      customButtonColor,
      customOnCollapsibleClicked,
    } = props;

    const [nextClicked, setNextClicked] = useState(false);
    const processed = nextClicked || touched;
    const Icon = processed
      ? valid
        ? StepComplete
        : StepError
      : StepNotComplete;
    const containerClasses = classnames('form-step-container', className, {
      'form-step-container--valid': valid && processed,
      'form-step-container--invalid': !valid && processed,
      'form-step-container--last': last,
    });
    const onNextClick = useCallback(() => {
      if (onNext) {
        onNext(nextAllowed);
      }
      setNextClicked(true);
    }, [nextAllowed, onNext]);
    const onCollapsibleClicked = useCallback(
      (originalClick: () => void, index: number) => {
        if (!collapsed) {
          originalClick();
        }
        customOnCollapsibleClicked?.(index);
      },
      [collapsed],
    );
    return (
      <div className={containerClasses} ref={ref}>
        <div className="form-step-container__border" />
        <Icon className="form-step-container__icon" />
        <div className="form-step-container__content">
          <CollapsibleContentContainerWithoutLocalState
            orderNumber={
              showOrderNumber ? (indexOffset + index).toString() : undefined
            }
            expandedOnMount={true}
            disableExpand={collapsed}
            containerName={header}
            className={contentClassName}
            onClickOverride={(originalClick: () => void) =>
              onCollapsibleClicked(originalClick, index - 1)
            }
            dropdownMenuContent={
              dropdownMenuContent || renderDropdownOptions?.()
            }
            useCrossIconStyle={useCrossIconStyle}
            proBadgeType={proBadgeType}
            isProcessed={processed}
            isExpanded={stepsStatus[index - 1]}
            toggleIsExpanded={value => {
              const updatedStepStatus = [...stepsStatus];
              updatedStepStatus[index - 1] = value;
              setStepsStatus(updatedStepStatus);
              if (value && onStepOpened) {
                onStepOpened(index - 1);
              }
            }}
          >
            {children}
            {onNext && !last && !hideNextButton && (
              <Button
                onClick={onNextClick}
                color={customButtonColor || ButtonColor.blue}
                css={{ container: 'form-step-container__next-button' }}
              >
                Next
              </Button>
            )}
          </CollapsibleContentContainerWithoutLocalState>
        </div>
      </div>
    );
  },
);

export default React.memo(FormStepContainer);
